import { Box, Typography } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useMemo } from 'react';

export interface SourceData {
  source: string;
  count: number;
  percentage: number;
  color?: string;
}

interface SourcesPieChartProps {
  data: SourceData[];
  isLoading: boolean;
  onSliceClick?: (source: string) => void;
}

const SourcesPieChart = ({ data, isLoading, onSliceClick }: SourcesPieChartProps) => {
  const hasData = Boolean(data && data.length > 0);
  data = data.sort((a, b) => b.count - a.count)
  const chartOption = useMemo(() => ({
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)',
    },
    legend: {
      orient: 'vertical',
      left: '48%',
      top: 'middle',
      itemWidth: 12,
      itemHeight: 12,
      itemStyle: { borderRadius: 0 },
      textStyle: { fontSize: 15, fontFamily: 'Montserrat' },
      formatter: (name: string) => {
        const item = (data ?? []).find((d) => d.source === name);
        return item ? `${name} - ${item.percentage.toFixed(1)}% (${item.count})` : name;
      },
    },
    series: [
      {
        name: 'Alerts',
        type: 'pie',
        radius: '75%',
        center: ['22%', '55%'],
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: { show: false },
        labelLine: { show: false },
        data: (data ?? []).map((item) => ({
          name: item.source,
          value: item.count,
          itemStyle: { color: item.color },
        })),
      },
    ],
  }), [data]);

  if (isLoading) return <LoaderComponent />;

  if (!hasData) {
    return <Box sx={{ width: '85%', p: 2, textAlign: 'center' }}>No data available</Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>Alert Trend by Sources</Typography>
      <ReactECharts
        option={chartOption}
        style={{ height: 570, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        onEvents={{
          click: (params: { name?: string }) => {
            if (params?.name) onSliceClick?.(params.name);
          }
        }}
        notMerge
      />
    </Box>
  );
};

export default SourcesPieChart;
